import {
  Center,
  Menu,
  MenuButton,
  type MenuButtonProps,
  MenuItem,
  MenuList,
  type PlacementWithLogical,
  Portal,
  Spinner,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { type IOption, MenuItemLink } from "@src/components/ui-kit";
import { budgetovacApi } from "@src/services/budgetovac";
import { observer } from "mobx-react-lite";
import type { Route } from "nextjs-routes";
import {
  type FunctionComponent,
  type PropsWithChildren,
  useState,
} from "react";

type Props = {
  menuPlacement?: PlacementWithLogical;
  listPlacement?: PlacementWithLogical;
  menuButtonProps?: MenuButtonProps;
  onItemClick?: (value: string) => void;
  queryParams?: Record<string, string | undefined>;
  createPathname?: Route["pathname"];
};

export const CreateBudgetMenu: FunctionComponent<PropsWithChildren<Props>> =
  observer(function CreateBudgetMenu({
    children,
    menuPlacement,
    listPlacement = "right-start",
    menuButtonProps,
    onItemClick,
    queryParams,
    createPathname = "/budgets/proposals/create",
  }) {
    const [fetchingTemplates, setFetchingTemplates] = useState(false);
    const [templates, setTemplates] = useState<IOption[]>([]);

    const handleTemplateClick = async () => {
      setFetchingTemplates(true);
      const { data } = await budgetovacApi.getTemplates();
      setFetchingTemplates(false);

      if (!data.length) return;

      setTemplates(
        data.map((template) => ({
          label: template.name,
          value: template.id.toString(),
        })),
      );
    };

    return (
      <Menu placement={menuPlacement}>
        <MenuButton {...menuButtonProps}>{children}</MenuButton>
        <MenuList>
          <MenuItemLink
            href={{ pathname: createPathname, query: queryParams }}
            onClick={() => {
              onItemClick?.("blank");
            }}
          >
            <Trans>Blank</Trans>
          </MenuItemLink>
          <MenuItem
            as={Menu}
            onClick={handleTemplateClick}
            placement={listPlacement}
          >
            <MenuButton
              w="full"
              px="3"
              py="1.5"
              textAlign="left"
              _hover={{ bgColor: "gray.100" }}
              onClick={handleTemplateClick}
            >
              <Trans>From template</Trans>
            </MenuButton>
            <Portal>
              <MenuList overflowY="auto" maxH="80">
                {fetchingTemplates ? (
                  <Center minH="8">
                    <Spinner size="sm" />
                  </Center>
                ) : templates.length ? (
                  templates.map((template) => (
                    <MenuItemLink
                      key={template.value + template.label}
                      href={{
                        pathname: createPathname,
                        query: {
                          ...queryParams,
                          budgetTemplateId: template.value,
                        },
                      }}
                      onClick={() => {
                        onItemClick?.(template.value);
                      }}
                    >
                      {template.label}
                    </MenuItemLink>
                  ))
                ) : (
                  <Center minH="8">
                    <Trans>No templates found</Trans>
                  </Center>
                )}
              </MenuList>
            </Portal>
          </MenuItem>
        </MenuList>
      </Menu>
    );
  });
